import * as React from 'react';
import * as styles from './Composer.css';
import { Text } from './text/Text';
import { Toolbox } from './toolbox/Toolbox';
import { Helper } from '../../Helper';
import { Image } from './image/Image';
import { IComposerComponent } from '../../interfaces/IComposerTool';
import { IComposerImage } from '../../interfaces/IComposerImage';
import { IComposerVideo } from '../../interfaces/IComposerVideo';
import { Video } from './video/Video';;
import { Audio } from './audio/Audio';
import { Gallery } from './gallery/Gallery';
import { IASpinner } from '../spinner/Spinner';
import { Cover } from './cover/Cover';
import { Store } from './stores';
import { inject, observer } from 'mobx-react';
import { LocalizationService } from '../../services/LocalizationService';
import { IAButton } from '../../components/button/Button';
import { IAIcon } from '../icon/Icon';
import { IAPanel } from '../../components/panels/Panel';
import { IAPivot } from '../../components/pivot/Pivot';
import { Settings } from './settings/Settings';
import { Preview } from './preview/Preview';

import MessageService from '../../services/MessageService';
import Utils from '../treeTargeting/utils';
import { TargetingService } from '../../services/TargetingService';
import { IMessage } from '../../interfaces/IMessage';
import { IAAttachments } from '../attachments/Attachments';
import { StockPhotosPanel } from '../stockPhotos/StockPhotosPanel';
import { Table } from './table/Table';
import FeedService from '../../services/FeedService';
import BotService from '../../services/BotService';
import { IInstance } from '../../interfaces/IInstance';
import { IHttpStatus } from '../../interfaces/IHttpStatus';
import { IAMessageBar } from '../messageBar/MessageBar';
import EventService from '../../services/EventService';
import { Replay } from './replay/Replay';
import { IComposerGallery } from '../../interfaces/IComposerGallery';
import { IMessageCanCreateMorePins } from '../../interfaces/IMessageCanCreateMorePins';
import { Environment } from '../../services/Environment';
import ImageService from '../../services/ImageService';
import { IAOrganizationalAssetPanel } from './organizationalAssetPanel/OrganizationalAssetPanel';
import { NewsLink } from './newsLink/NewsLink';
import { Teaser } from './teaser/Teaser';
import { CategorySelector } from './categorySelector/CategorySelector';
import { Author } from './author/Author';
import Moment from 'react-moment';
import { IComposerAudio } from '../../interfaces/IComposerAudio';

export interface IProps {
  store?: Store;
  open: boolean;
  close: (message: IMessage) => boolean;
  closeAndReload: () => void;
  messageSaved: (message: IMessage) => void;
  delete: () => void;
  isReloadOk: (message: IMessage) => boolean;
  saveTemplateCallback?: (jsonTemplate: string) => void;
}

export interface IState {
  imageViewerImages: IComposerImage[];
  imageViewerIndex: number;
  isCheckingBotStatus: boolean;
  canCreateMorePin: IMessageCanCreateMorePins;
  showDraftValidationError: boolean;
  draftSavedMessageUpdated?: Date;
}

@inject('store')
@observer
export class Composer extends React.Component<IProps, IState> {

  // private readonly dateTimeService: DateTimeService = new DateTimeService();
  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      imageViewerImages: undefined,
      imageViewerIndex: 0,
      isCheckingBotStatus: false,
      canCreateMorePin: undefined,
      showDraftValidationError: false
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    document.body.addEventListener('click', (event: any) => {
      var exceptions = '#IA_InlineToolbox *';
      if (!event.target.matches(exceptions)) {
        this.props.store.showLineToolbox = false;
      }
    });
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.open !== this.props.open) {
      this.setupComposer(props);
    }
  }

  public componentDidMount(): void {
    document.addEventListener("keydown", (event) => this.keyListener(event), false);
    window.onbeforeunload = (e) => {
      if (this.props.isReloadOk(this.convertToMessage())) {
        // Cancel the event
        e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    };
    if (this.props.store.isMessagesTemplateEditor) {
      this.setupComposer(this.props);
    }
  }

  private setupComposer(props: IProps) {
    if (this.props.store.isNewsLink) {
      this.props.store.selectedComposerTab = "newsLink";
    } else {
      this.props.store.selectedComposerTab = "content";
    }
    this.props.store.enableValidation = false;
    this.props.store.getCategories(true);
    if (this.props.store.creatingNewMessage) {
      this.props.store.likesEnabled = true;
      this.props.store.commentsEnabled = true;
    }
    if (this.props.store.contentComponents.length === 0) {
      if (this.props.store.htmlContent) {
        this.convertOldHtmlToContentComponents()
      }
    }
    if ((this.props.store.contacts && (this.props.store.contacts.users.length === 0 || this.props.store.contacts.users[0] == undefined)) && !this.props.store.isMessagesTemplateEditor) {
      this.props.store.contacts.users.push(
        this.props.store.user
      );
    }
    this.props.store.getTargetingApplication().then(() => {
      this.props.store.getTargetingProperties().then(() => this.forceUpdate());
    });
    if (props.open) {
      FeedService.getFeeds(this.props.store.environment, this.props.store.tenant, this.props.store.token, this.props.store.component === "Messages" ? "messages" : "events", this.props.store.instance.instanceName).then(feeds => {
        this.props.store.feedOptions = [...feeds];
        this.forceUpdate();
      });
      const isPinEnabled = this.props.store.instance?.settings?.enablePinMessages;
      if (isPinEnabled && !this.props.store.isPinned) {
        MessageService.getCanCreateMorePins(this.props.store.environment, this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName).then(response => {
          this.setState({ canCreateMorePin: response });
        });
      }
      if (!this.state.isCheckingBotStatus) {
        this.setState({ isCheckingBotStatus: true });
        BotService.getNewBotInstallationStatus(this.props.store.environment, this.props.store.tenant).then(botStatus => {
          this.props.store.isBotInstalled = botStatus.isInstalled;
        }).catch(() => {
          this.props.store.isBotInstalled = false;
        });
      }
      const appNotificationEnabledOnInstance = this.props.store?.instance?.pushEnabled && this.props.store.component === "Messages";
      const plingNotificationEnabledOnMessageInstance = this.props.store?.instance?.settings?.enablePushFromPling && this.props.store.component === "Messages";

      // should only respect default settings when not in template editor or creating a message from template
      if (!this.props.store.isMessagesTemplateEditor && !this.props.store.isCreatingFromTemplate) {
        this.props.store.push = this.props.store.pushOriginal ?? (appNotificationEnabledOnInstance && this.props.store?.instance?.settings?.pushByDefault);
        this.props.store.sendUsingBot = this.props.store.sendUsingBotOriginal ?? (plingNotificationEnabledOnMessageInstance && this.props.store?.instance?.settings?.plingByDefault);
      }
    } else {
      this.setState({ canCreateMorePin: undefined });
    }
    if (this.props.store.creatingNewMessage && this.props.store.instance && this.props.store.instance.settings && this.props.store.instance.settings.shouldByDefaultExpireAfterAmountOfDays != undefined) {
      let defaultEndDate = new Date();
      defaultEndDate.setDate(defaultEndDate.getDate() + this.props.store.instance.settings.shouldByDefaultExpireAfterAmountOfDays);
      defaultEndDate = new Date(defaultEndDate.setHours(0, 0, 0, 0));
      if (!this.props.store.endDate) {
        this.props.store.endDate = defaultEndDate;
      }
    }
  }

  private keyListener(event: KeyboardEvent): void {
    if (event.keyCode === 13 && this.props.store?.activeComponent?.type === "teaser") {
      event.preventDefault();
    }
  }

  private convertOldHtmlToContentComponents(): void {
    var div = document.createElement('div');
    let htmlContent = this.props.store.htmlContent?.trim();
    htmlContent = htmlContent.replace(new RegExp('\n', 'g'), ``);
    htmlContent = htmlContent.replace(/<\s*p.*?>/, '<p>');
    div.innerHTML = htmlContent;
    var paragraphs3 = div.getElementsByTagName("p");
    for (var index = 0; index < paragraphs3.length; index++) {
      paragraphs3[index].removeAttribute("class");
    }
    htmlContent = div.innerHTML;
    htmlContent = htmlContent.replace(new RegExp('<ol>', 'g'), `<composer><ol>`);
    htmlContent = htmlContent.replace(new RegExp('</ol>', 'g'), `</ol></composer>`);
    htmlContent = htmlContent.replace(new RegExp('<ul>', 'g'), `<composer><ul>`);
    htmlContent = htmlContent.replace(new RegExp('</ul>', 'g'), `</ul></composer>`);
    htmlContent = htmlContent.replace(new RegExp('<p>', 'g'), `<composer><p>`);
    htmlContent = htmlContent.replace(new RegExp('</p>', 'g'), `</p></composer>`);
    htmlContent = htmlContent.replace(new RegExp('<li><composer><p>', 'g'), `<li>`);
    htmlContent = htmlContent.replace(new RegExp('</p></composer></li>', 'g'), `</li>`);
    div.innerHTML = htmlContent;
    var paragraphs = div.getElementsByTagName("composer");
    for (var i = 0; i < paragraphs.length; i++) {
      var iframes = paragraphs[i].getElementsByTagName("iframe");
      var images = paragraphs[i].getElementsByTagName("img");
      if (iframes.length > 0) {
        for (var iframesIndex = 0; iframesIndex < iframes.length; iframesIndex++) {
          this.props.store.addComponent({
            type: "video",
            video: {
              source: "Link",
              posterImage: {
                source: "Upload"
              },
              url: iframes[iframesIndex].src
            }
          });
        }
      } else if (images.length > 0) {
        for (var imagesIndex = 0; imagesIndex < images.length; imagesIndex++) {
          this.props.store.addComponent({
            type: "image",
            image: {
              source: "Link",
              url: images[imagesIndex].src
            }
          });
        }
      } else {
        this.props.store.addComponent({
          type: "text",
          text: paragraphs[i].innerHTML
        });
      }
    }
  }

  private renderContentComponents(): JSX.Element[] {
    const components = [];
    this.props.store.contentComponents.forEach((component: IComposerComponent, index) => {
      switch (component.type) {
        case "text":
          components.push(
            <Text
              key={component.id}
              index={index}
              id={component.id}
              text={component.text}
              boxStyle={component.style}
              hideToolbox={this.props.store.activeComponent == undefined || (this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id)}
              onChange={(text: string) => {
                component.text = text;
              }}
              onStyleChange={(style: any) => {
                component.style = style;
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
              }}
              showDropZones={this.props.store.componentToAdd != undefined}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                // this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => {
                this.props.store.componentToAdd = undefined;
                this.forceUpdate();
              }}
            />
          );
          break;
        case "image":
          components.push(
            <Image
              image={component.image ? component.image : undefined}
              key={component.id}
              index={index}
              id={component.id}
              hideToolbox={this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id}
              showDropZones={this.props.store.componentToAdd != undefined}
              onChange={(image: IComposerImage) => {
                if (component.id === this.props.store.activeComponent.id) {
                  this.props.store.activeComponent.image = image;
                  if (this.props.store.tenantSettings?.admin?.useBlobImages && !this.props.store.isMessagesTemplateEditor) {
                    Helper.getImageBlobUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.activeComponent.image.url, this.props.store.activeComponent.image.modified, "Full", this.props.store.activeComponent.image?.cropSettings).then((blobUrl) => {
                      this.props.store.activeComponent.image.imageBlobUrl = blobUrl;
                      this.forceUpdate();
                    })
                  } else {
                    this.forceUpdate();
                  }

                }
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
              }}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => this.props.store.componentToAdd = undefined}
            />
          );
          break;
        case "gallery":
          components.push(
            <Gallery
              gallery={component.gallery ? component.gallery : undefined}
              key={component.id}
              index={index}
              id={component.id}
              hideToolbox={this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id}
              showDropZones={this.props.store.componentToAdd != undefined}
              onChange={(gallery: IComposerGallery) => {
                if (component.id === this.props.store.activeComponent.id) {
                  this.props.store.activeComponent.gallery = gallery;
                }
              }}
              openImageViewer={(imageViewerImages: IComposerImage[]) => {
                this.setState({ imageViewerImages });
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
              }}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => this.props.store.componentToAdd = undefined}
            />
          );
          break;
        case "video":
          components.push(
            <Video
              video={component.video ? component.video : undefined}
              key={component.id}
              index={index}
              id={component.id}
              hideToolbox={this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id}
              showDropZones={this.props.store.componentToAdd != undefined}
              onChange={(video: IComposerVideo) => {
                if (component.id === this.props.store.activeComponent.id) {
                  this.props.store.activeComponent.video = video;
                  this.forceUpdate();
                }
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
              }}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => this.props.store.componentToAdd = undefined}
            />
          );
          break;
        case "audio":
          components.push(
            <Audio
              audio={component.audio ? component.audio : undefined}
              key={component.id}
              index={index}
              id={component.id}
              hideToolbox={this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id}
              showDropZones={this.props.store.componentToAdd != undefined}
              onChange={(audio: IComposerAudio) => {
                if (component.id === this.props.store.activeComponent.id) {
                  this.props.store.activeComponent.audio = audio;
                  this.forceUpdate();
                }
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
              }}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => this.props.store.componentToAdd = undefined}
            />
          );
          break;
        case "table":
          components.push(
            <Table
              key={component.id}
              index={index}
              id={component.id}
              table={component.table}
              hideToolbox={this.props.store.activeComponent == undefined || (this.props.store.activeComponent && component.id !== this.props.store.activeComponent.id)}
              onChange={(table: string) => {
                this.props.store.activeComponent.table = table;
                this.forceUpdate();
              }}
              onFocus={() => {
                this.props.store.activeComponent = component;
                setTimeout(() => {
                  this.forceUpdate();
                }, 0);
              }}
              showDropZones={this.props.store.componentToAdd != undefined}
              onDrag={() => {
                this.props.store.componentToAdd = component;
                this.props.store.startIndex = index;
                this.props.store.activeComponent = undefined;
              }}
              onDragEnd={() => {
                this.props.store.componentToAdd = undefined;
                this.forceUpdate();
              }}
            />
          );
          break;
        default:
          break;
      }
    });
    return components;
  }

  private convertToHtml(): string {
    let htmlString: string = "";
    this.props.store.contentComponents.forEach((component: IComposerComponent) => {
      switch (component.type) {
        case "text":
          if (component.text !== undefined && component.text !== "<p><br></p>") {
            htmlString += component.text;
          }
          break;
        case "image":
          htmlString += `<img src="${component.image.url}" />`;
      }
    });
    return htmlString !== "" ? htmlString : undefined;
  }

  private convertToMessage(): IMessage {
    this.props.store.contentComponents.forEach((contentComponent: IComposerComponent) => {
      if (contentComponent.type === "text" && contentComponent.text) {
        contentComponent.text = contentComponent.text.replace(/rel="noopener noreferrer"/g, 'data-interception="off"');
      }
    });

    // fix for templates using user profile targeting 
    let targetingFields = undefined; // has to be "any" type because the old targeting is stupid
    if (this.props.store.applicationSettings?.applicationSettings?.toLowerCase() === "profile data") {
      targetingFields = this.props.store.targetingFields;
    }
    const isRecurringMessage = this.props.store.component === "Messages" && this.props.store.recurrenceInfo?.isRecurring;
    return {
      author: this.props.store.author || this.props.store.user,
      id: this.props.store.id,
      title: this.props.store.title?.text ? this.props.store.title?.text : undefined,
      teaser: this.props.store.instance?.settings?.enableTeaser && this.props.store.teaser?.text ? this.props.store.teaser?.text : "",
      location: this.props.store.location?.text ? this.props.store.location?.text : undefined,
      content: this.convertToHtml(),
      startDate: this.props.store.startDate,
      endDate: this.props.store.endDate,
      allDayEvent: this.props.store.allDayEvent,
      contacts: this.props.store.contacts?.users,
      iaTargetingTargetContent: this.props.store.iaTargetingTargetContent || (this.props.store.instance?.settings?.targetingRequired != undefined && this.props.store.instance?.settings?.targetingRequired),
      iaTargetingOperator: this.props.store.iaTargetingOperator ? this.props.store.iaTargetingOperator : "OR",
      iaTargetingGroupData: this.props.store.iaTargetingGroupData ? this.props.store.iaTargetingGroupData : "OR",
      image: this.props.store.cover.image.url,
      categories: this.props.store.subjects?.categories,
      videoUrl: this.props.store.cover?.video?.url,
      videoEmbedCode: this.props.store.cover?.video?.embedCode,
      isDraft: this.props.store.isDraft,
      iaTargetingOperatorADGroups: this.props.store.iaTargetingOperatorADGroups,
      iaConfiguration: {
        cropSettings: this.props.store.cover?.image?.cropSettings,
        headerImageAltText: this.props.store.cover?.image?.altText,
        headerVideoAltText: this.props.store.cover?.video?.altText,
        disableLikes: isRecurringMessage ? true : !this.props.store.likesEnabled,
        disableComments: isRecurringMessage ? true : !this.props.store.commentsEnabled,
        attachments: this.props.store.attachments?.files,
        composerComponents: this.props.store.contentComponents,
        onlyShowCoverImageInRollup: this.props.store.cover?.image?.onlyShowCoverImageInRollup,
        replay: this.props.store.configuration?.replay,
        editor: "froala",
        isNewsLink: this.props.store.isNewsLink,
        newsLink: this.props.store.newsLink,
        openNewsLinkInNewTab: this.props.store.openNewsLinkInNewTab,
        templateName: this.props.store.configuration.templateName
      },
      approvalStatus: this.props.store.approvalStatus,
      approvers: this.props.store.approvers,
      sendUsingBot: isRecurringMessage ? false : this.props.store.sendUsingBot,
      push: isRecurringMessage ? false : this.props.store.push,
      feeds: this.props.store.selectedFeeds?.join(","),
      isPinned: this.props.store.isPinned,
      pinnedEndDate: this.props.store.pinnedEndDate,
      targetingFields: targetingFields,
      recurrence: this.props.store.recurrenceInfo?.isRecurring,
      recurrenceData: this.props.store.recurrenceInfo?.recurrenceData
    };
  }

  private isCurrentUserApproverOrAdmin() {
    const isApprover = this.props.store.approvers?.filter(i => !i.disabled && this.props.store.user && (i.userPrincipalName?.toLocaleLowerCase() === this.props.store.user.userPrincipalName?.toLocaleLowerCase() || (i.email && i.email?.toLocaleLowerCase() === this.props.store.user.email?.toLocaleLowerCase()))).length > 0;
    const isAdmin = this.props.store.userRole === "Administrator";
    return isApprover || isAdmin;
  }

  private save(): Promise<IHttpStatus> {
    this.props.store.serverAlert = undefined;
    return new Promise<IHttpStatus>(async (resolve: (httpStatus: IHttpStatus) => void): Promise<void> => {
      if (!this.props.store.isSavingDraft) {
        this.props.store.isSaving = true;
      }
      // Approval
      if (this.props.store.instance && this.props.store.instance.approvalEnabled) {
        let isCurrentUserApproverOrAdmin: boolean = this.isCurrentUserApproverOrAdmin();
        if (isCurrentUserApproverOrAdmin) {
          this.props.store.approvalStatus = "Approved";
        } else {
          this.props.store.approvalStatus = "Pending";
        }
      }
      const message: IMessage = this.convertToMessage();
      console.log(message);

      if (this.props.store.creatingNewMessage) {
        switch (this.props.store.component) {
          case "Messages":
            const targetingGroupData = this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() !== "profile data"
              ? TargetingService.getTargetingGroupOptions(this.props.store.iaTargetingGroupData, "Messages", this.props.store.instance.instanceName, "IAMessageTargetingGroupData")
              : null;
            MessageService.createMessage(message, this.props.store.environment != undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingGroupData).then(async (result: { httpStatus: IHttpStatus, id?: number }) => {
              if (result.httpStatus.statusCode === 200) {
                this.props.store.creatingNewMessage = false;
                this.props.store.id = result.id;
                message.id = result.id;
                this.props.messageSaved(message);
                let targetingValues: any = this.props.store.targetingFields;
                if (this.props.store.iaTargetingTargetContent || this.props.store.instance?.settings?.targetingRequired) {
                  if (this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() === "profile data" && targetingValues) {
                    // Getting Targeting Properties
                    targetingValues = Utils.validateProperties(targetingValues, this.props.store.targetingProperties);
                    // Saving targeting
                    await TargetingService.updateTermFields(message, "messages", this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingValues).then((httpStatus: IHttpStatus) => {
                      if (httpStatus.statusCode !== 200) {
                        if (this.props.store.serverAlert == undefined) {
                          this.props.store.serverAlert = {
                            statusCode: httpStatus.statusCode,
                            message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                          };
                        }
                        resolve(httpStatus);
                      }
                    });
                  }
                }
                resolve(result.httpStatus);
              } else {
                if (this.props.store.serverAlert == undefined) {
                  this.props.store.serverAlert = {
                    statusCode: result.httpStatus.statusCode,
                    message: this.localizationService.strings.Composer_ServerErrorCreateMessage + this.props.store.getServerError(result.httpStatus.statusCode)
                  };
                }
                resolve(result.httpStatus);
              }
              this.props.store.isSaving = false;
            });
            // prepare image renditions
            if (this.props.store.tenantSettings?.admin?.useBlobImages) {
              try {
                ImageService.PrepareImageRenditions(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.token, this.props.store.cover.image.url, this.props.store.cover.image.modified, this.props.store.cover.image.cropSettings);
              } catch (error) { console.log(error) }
            }
            break;
          case "Event":
            EventService.createEvent(message, this.props.store.environment != undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName).then(async (result: { httpStatus: IHttpStatus, id?: string }) => {
              if (result.httpStatus.statusCode === 200) {
                this.props.store.id = parseInt(result.id);
                message.id = parseInt(result.id);
                this.props.messageSaved(message);
                let targetingValues: any = this.props.store.targetingFields;
                if (this.props.store.iaTargetingTargetContent || this.props.store.instance?.settings?.targetingRequired) {
                  if (this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() === "profile data") {
                    // Getting Targeting Properties
                    targetingValues = Utils.validateProperties(targetingValues, this.props.store.targetingProperties);
                    // Saving targeting
                    await TargetingService.updateTermFields(message, "events", this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingValues).then((httpStatus: IHttpStatus) => {
                      if (httpStatus.statusCode !== 200) {
                        if (this.props.store.serverAlert == undefined) {
                          this.props.store.serverAlert = {
                            statusCode: httpStatus.statusCode,
                            message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                          };
                        }
                        resolve(httpStatus);
                      }
                    });
                  } else {
                    await TargetingService.updateTargetingGroup(this.props.store.iaTargetingGroupData, "Events",
                      this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant,
                      this.props.store.instance.instanceName, this.props.store.id, "IAEventTargetingGroupData").then((httpStatus: IHttpStatus) => {
                        if (httpStatus.statusCode !== 200) {
                          if (this.props.store.serverAlert == undefined) {
                            this.props.store.serverAlert = {
                              statusCode: httpStatus.statusCode,
                              message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                            };
                          }
                          resolve(httpStatus);
                        }
                      });
                  }
                }
                resolve(result.httpStatus);
              } else {
                if (this.props.store.serverAlert == undefined) {
                  this.props.store.serverAlert = {
                    statusCode: result.httpStatus.statusCode,
                    message: this.localizationService.strings.Composer_ServerErrorCreateMessage + this.props.store.getServerError(result.httpStatus.statusCode)
                  };
                }
                resolve(result.httpStatus);
              }
              this.props.store.isSaving = false;
            });
            break;
          default:
            throw new Error("Not implemented");
        }
      } else {
        switch (this.props.store.component) {
          case "Messages":
            const isBeingPublished = this.isMessagePublishedFirstTime();
            const targetingGroupData = this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() !== "profile data"
              ? TargetingService.getTargetingGroupOptions(this.props.store.iaTargetingGroupData, "Messages", this.props.store.instance.instanceName, "IAMessageTargetingGroupData")
              : null;
            MessageService.updateMessage(message, this.props.store.environment != undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingGroupData, isBeingPublished).then(async (httpStatus: IHttpStatus) => {
              if (httpStatus.statusCode === 200) {
                this.props.messageSaved(message);
                let targetingValues: any = this.props.store.targetingFields;
                if (this.props.store.iaTargetingTargetContent || this.props.store.instance?.settings?.targetingRequired) {
                  if (this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() === "profile data") {
                    // Getting Targeting Properties
                    targetingValues = Utils.validateProperties(targetingValues, this.props.store.targetingProperties);
                    // Saving targeting
                    await TargetingService.updateTermFields(message, "messages", this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingValues).then((httpStatus: IHttpStatus) => {
                      if (httpStatus.statusCode !== 200) {
                        if (this.props.store.serverAlert == undefined) {
                          this.props.store.serverAlert = {
                            statusCode: httpStatus.statusCode,
                            message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                          };
                        }
                        resolve(httpStatus);
                      }
                    });
                  }
                }
                resolve(httpStatus);
              } else {
                if (this.props.store.serverAlert == undefined) {
                  this.props.store.serverAlert = {
                    statusCode: httpStatus.statusCode,
                    message: this.localizationService.strings.Composer_ServerErrorUpdateMessage + this.props.store.getServerError(httpStatus.statusCode)
                  };
                }
                resolve(httpStatus);
              }
              this.props.store.isSaving = false;
            });
            if (this.props.store.tenantSettings?.admin?.useBlobImages) {
              try {
                ImageService.PrepareImageRenditions(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.token, this.props.store.cover.image.url, this.props.store.cover.image.modified, this.props.store.cover.image.cropSettings);
              } catch (error) { console.log(error) }
            }
            break;
          case "Event":
            EventService.updateEvent(message, this.props.store.environment != undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName).then(async (httpStatus: IHttpStatus) => {
              if (httpStatus.statusCode === 200) {
                this.props.messageSaved(message);
                let targetingValues: any = this.props.store.targetingFields;
                if (this.props.store.iaTargetingTargetContent || this.props.store.instance?.settings?.targetingRequired) {
                  if (this.props.store.applicationSettings?.applicationSettings?.toLocaleLowerCase() === "profile data") {
                    // Getting Targeting Properties
                    targetingValues = Utils.validateProperties(targetingValues, this.props.store.targetingProperties);
                    // Saving targeting
                    await TargetingService.updateTermFields(message, "events", this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant, this.props.store.instance.instanceName, targetingValues).then((httpStatus: IHttpStatus) => {
                      if (httpStatus.statusCode !== 200) {
                        if (this.props.store.serverAlert == undefined) {
                          this.props.store.serverAlert = {
                            statusCode: httpStatus.statusCode,
                            message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                          };
                        }
                        resolve(httpStatus);
                      }
                    });
                  } else {
                    await TargetingService.updateTargetingGroup(this.props.store.iaTargetingGroupData, "Events",
                      this.props.store.environment !== undefined ? this.props.store.environment : "Production", this.props.store.token, this.props.store.tenant,
                      this.props.store.instance.instanceName, this.props.store.id, "IAEventTargetingGroupData").then((httpStatus: IHttpStatus) => {
                        if (httpStatus.statusCode !== 200) {
                          if (this.props.store.serverAlert == undefined) {
                            this.props.store.serverAlert = {
                              statusCode: httpStatus.statusCode,
                              message: this.localizationService.strings.Composer_ServerErrorTargetingFailed + this.props.store.getServerError(httpStatus.statusCode)
                            };
                          }
                          resolve(httpStatus);
                        }
                      });
                  }
                }
                resolve(httpStatus);
              } else {
                if (this.props.store.serverAlert == undefined) {
                  this.props.store.serverAlert = {
                    statusCode: httpStatus.statusCode,
                    message: this.localizationService.strings.Composer_ServerErrorUpdateMessage + this.props.store.getServerError(httpStatus.statusCode)
                  };
                }
                resolve(httpStatus);
              }
              this.props.store.isSaving = false;
            });
            break;
          default:
            throw new Error("Not implemented");
        }
      }
    });
  }

  // determine if message is published for the first time
  private isMessagePublishedFirstTime() {
    if (this.props.store.instance && this.props.store.instance.approvalEnabled) {
      if (this.props.store.originalApprovalStatus === "Approved") {
        // if an admin has created the draft, the original approval status will be Approved
        return !this.props.store.isDraft && this.props.store.isDraftOriginally;
      } else {
        // if the original approval status is not Approved, check if it is now being approved
        return this.props.store.approvalStatus === "Approved" && !this.props.store.isDraft;
      }
    } else {
      return !this.props.store.isDraft && this.props.store.isDraftOriginally;
    }
  }

  private getValidationErrors(): string[] {
    const errorList: string[] = [];
    if (this.props.store.selectedComposerTab !== "preview") {
      if (!this.props.store.validateTitle()) {
        errorList.push(this.localizationService.strings.Composer_ValidateTitle);
      }
      if (!this.props.store.validateSubjects()) {
        errorList.push(this.localizationService.strings.Composer_ValidateSubjects);
      }
      if (!this.props.store.validateContacts()) {
        errorList.push(this.localizationService.strings.Composer_ValidateContacts);
      }
      if (!this.props.store.validateTeaser()) {
        errorList.push(this.localizationService.strings.Composer_ValidateTeaser);
      }
      if (!this.props.store.validateTargeting()) {
        errorList.push(this.localizationService.strings.Composer_ValidateTargeting);
      }
      if (!this.props.store.validatePinDate()) {
        errorList.push(this.localizationService.strings.Composer_ValidatePin);
      }
      if (!this.props.store.validateApprovers()) {
        errorList.push(this.localizationService.strings.Composer_ValidateApprovers);
      }
      if (!this.props.store.validateReplayFeed()) {
        errorList.push(this.localizationService.strings.Composer_ReplayFeedValidation);
      }
      if (!this.props.store.validateReplayContentHeight()) {
        errorList.push(this.localizationService.strings.Composer_ReplayContentTooLong);
      }
    }
    return errorList;
  }

  public render(): JSX.Element {
    let publishButtonLabel = this.props.store.creatingNewMessage ? this.localizationService.strings.Composer_PublishButton : (this.props.store.isDraft ? this.localizationService.strings.Composer_PublishButton : this.localizationService.strings.Composer_PublishChangesButton);
    let showRejectButton = false;
    // Approval
    if (this.props.store.instance && this.props.store.instance.approvalEnabled) {
      const isCurrentUserApproverOrAdmin: boolean = this.isCurrentUserApproverOrAdmin();
      if (this.props.store.component === "Messages" && !isCurrentUserApproverOrAdmin) {
        publishButtonLabel = this.localizationService.strings.Panel_NavigatorBar_SubmitForApproval;
      }
      if (this.props.store.component === "Messages" && isCurrentUserApproverOrAdmin && this.props.store.approvalStatus !== "Approved" && !this.props.store.creatingNewMessage) {
        publishButtonLabel = this.localizationService.strings.Panel_NavigatorBar_ReviewApprove;
        showRejectButton = true;
      }
    }
    const isVideo = this.props.store?.activeComponent?.type === "video" || (this.props.store?.activeComponent?.type === "cover" && this.props.store?.activeComponent?.typeToEdit === "video");
    const instance: IInstance = this.props.store.instance ? JSON.parse(JSON.stringify(this.props.store.instance)) : undefined; // remove reference to props.store.instance
    if (instance?.settings?.enablePrinting) {
      // never show print icon while inside composer
      instance.settings.enablePrinting = false;
    }

    let disableSaveButtons = (this.props.store.enableValidation && !this.props.store.validate()) || this.props.store.isUploadingVideoOrImage;
    if (!((this.props.store.publishToReplay && this.props.store.selectedFeeds?.length > 0 && !this.props.store.replayContentTooHigh) || !this.props.store.publishToReplay)) {
      disableSaveButtons = false;
    }
    const tabs: { key: string, text: string, disabled?: boolean, icon?: string, error?: boolean, unselectedBackgroundColor?: string }[] = [];

    if (this.props.store.isNewsLink) {
      tabs.push(...[{
        key: 'newsLink',
        text: this.localizationService.strings.Composer_TabCompose?.toUpperCase(),
        icon: "Design"
      },
      {
        key: 'settings',
        text: this.localizationService.strings.Composer_TabSettings?.toUpperCase(),
        icon: "Control Panel",
        error: !this.props.store.validateSettingsTab()
      }]);
    } else {
      tabs.push(...[{
        key: 'content',
        text: this.localizationService.strings.Composer_TabCompose?.toUpperCase(),
        icon: "Design",
        error: !this.props.store.validateComposeTab()
      },
      {
        key: 'settings',
        text: this.localizationService.strings.Composer_TabSettings?.toUpperCase(),
        icon: "Control Panel",
        error: !this.props.store.validateSettingsTab()
      },
      {
        key: 'preview',
        text: this.localizationService.strings.Composer_TabPreview?.toUpperCase(),
        icon: "Preview"
      }]);

      const showFeedSettings = (this.props.store.userRole === "Administrator") || this.props.store.instance?.settings?.shareSettingRequiredUserRole == undefined || (this.props.store.instance?.settings?.shareSettingRequiredUserRole === "Members" && this.props.store.userRole === "Editor");

      if (this.props.store.feedOptions?.length > 0 && showFeedSettings) {
        tabs.push({
          key: 'replay',
          text: this.localizationService.strings.Composer_TabShare?.toUpperCase(),
          icon: "RSS",
          error: !this.props.store.validateReplayTab(),
          unselectedBackgroundColor: this.props.store.publishToReplay ? undefined : "#cdcdcd"
        });
      }
    }
    return (
      <div>
        <IAPanel
          open={this.props.store.open}
          transition={"slide"}
          highlightColor={this.props.store.highlightColor}
          showNavigationBar={this.props.store.isGalleryImagesUploading ? false : true}
          marginTop={this.props.store.marginTop}
          isMobile={this.props.store.isMobile}
          loading={this.props.store.isSaving}
          instance={instance}
          width={this.props.store.isMobile ? window.innerWidth : 1000}
          dataAutomationIdPrefix={`${this.props.store.dataAutomationIdPrefix}`}
          navigationsBarContent={
            <>
              {this.props.store.isMessagesTemplateEditor &&
                <div
                  style={{
                    display: "flex",
                    height: 44,
                    flexDirection: "row-reverse",
                    alignItems: "center"
                  }}
                >
                  <IAButton
                    borderRadius={5}
                    disbaled={disableSaveButtons}
                    buttonColor={this.props.store.highlightColor}
                    label={this.localizationService.strings.Messages_Template_SaveTemplate}
                    onClick={() => this.props.saveTemplateCallback && this.props.saveTemplateCallback(JSON.stringify(this.convertToMessage()))}
                  />
                </div>
              }
              {!this.props.store.isMessagesTemplateEditor &&
                <div className={styles.IA_componerNavigatorContent}>
                  <IAButton
                    label={publishButtonLabel}
                    buttonColor={this.props.store.highlightColor}
                    disbaled={disableSaveButtons}
                    borderRadius={5}
                    onClick={async () => {
                      this.setState({ showDraftValidationError: false });
                      this.props.store.enableValidation = true;
                      if (this.props.store.validate()) {
                        this.props.store.activeComponent = undefined;
                        this.props.store.isDraft = false;

                        if (this.props.store.recurrenceInfo?.isEditingOccurrence) {
                          try {
                            await EventService.addExceptionToRecurrence(this.props.store.environment, this.props.store.tenant, this.props.store.instance.instanceName, this.props.store.recurrenceInfo?.occurrenceSeriesId, this.props.store.recurrenceInfo?.occurrenceStartDate);
                            this.props.store.recurrenceInfo = undefined;
                          } catch (error) {
                            console.error(error);
                          }
                        }

                        this.save().then((httpStatus: IHttpStatus) => {
                          this.setState({ draftSavedMessageUpdated: undefined });
                          this.props.store.publishToReplay = false;
                          this.props.store.replayContentTooHigh = false;
                          if (httpStatus.statusCode === 200) {
                            this.props.closeAndReload();
                          }
                        });
                      }
                    }}
                    style={{
                      float: "right",
                      marginTop: 7,
                    }}
                  />
                  {showRejectButton &&
                    <IAButton
                      label={this.localizationService.strings.Panel_NavigatorBar_ReviewReject}
                      disbaled={disableSaveButtons}
                      buttonColor={"#df4f4f"}
                      borderRadius={5}
                      onClick={() => {
                        this.props.store.approvalStatus = "Rejected";
                        this.props.store.enableValidation = true;
                        if (this.props.store.validate()) {
                          this.save().then((httpStatus: IHttpStatus) => {
                            if (httpStatus.statusCode === 200) {
                              this.props.store.publishToReplay = false;
                              this.props.store.replayContentTooHigh = false;
                              this.setState({ draftSavedMessageUpdated: undefined });
                              this.props.closeAndReload();
                            }
                          });
                        }
                      }}
                      style={{
                        float: "right",
                        marginTop: 7,
                        marginRight: 15
                      }}
                    />
                  }
                  {!(this.props.store.isMobile && this.props.store.approvalStatus === "Pending") &&
                    <IAButton
                      buttonColor={"#8b8b8b"}
                      showSpinner={this.props.store.isSavingDraft}
                      borderRadius={5}
                      label={(this.props.store.creatingNewMessage ? this.localizationService.strings.Composer_DraftButton : ((this.props.store.isDraft || this.props.store.component === "Messages" && this.props.store.instance && this.props.store.instance.approvalEnabled && this.props.store.approvalStatus !== "Approved") ? this.localizationService.strings.Composer_DraftButton : this.localizationService.strings.Composer_SaveAndUnpublishButton))}
                      onClick={() => {
                        if (this.props.store.validateForDraft()) {
                          this.props.store.isDraft = true;
                          this.setState({ showDraftValidationError: false });
                          this.props.store.enableValidation = true;
                          this.props.store.isSavingDraft = true;
                          this.save().then((httpStatus: IHttpStatus) => {
                            this.props.store.creatingNewMessage = false;
                            this.props.store.isSavingDraft = false;
                            if (httpStatus.statusCode === 200) {
                              this.props.store.publishToReplay = false;
                              this.props.store.replayContentTooHigh = false;
                              this.props.store.isDraftOriginally = true;
                              this.setState({ draftSavedMessageUpdated: new Date() });
                            }
                          });
                        } else {
                          this.setState({ showDraftValidationError: true });
                        }
                      }}
                      style={{
                        float: "right",
                        marginTop: 7,
                        marginRight: 15
                      }}
                    />
                  }
                  {!this.props.store.creatingNewMessage &&
                    <IAButton
                      buttonColor="#df4f4f"
                      label={this.localizationService.strings.Panel_NavigatorBar_Delete}
                      disbaled={disableSaveButtons}
                      borderRadius={5}
                      onClick={() => {
                        this.props.store.activeComponent = undefined;
                        this.props.delete();
                      }}
                      style={{
                        float: "right",
                        marginTop: 7,
                        marginRight: 15
                      }}
                    />
                  }
                  {this.props.store.isGalleryImagesUploading &&
                    <div
                      className={styles.IA_uploadingGalleryImagesOverlay}
                    >
                    </div>
                  }
                  {this.props.store.recurrenceInfo?.isEditingOccurrence && this.props.store.component === "Event" &&
                    <IAButton
                      label={"Remove occurrence"}
                      buttonColor="#df4f4f"
                      disbaled={disableSaveButtons}
                      borderRadius={5}
                      onClick={() => {
                        this.props.store.loading = true;
                        EventService.addExceptionToRecurrence(this.props.store.environment, this.props.store.tenant, this.props.store.instance.instanceName, this.props.store.recurrenceInfo?.occurrenceSeriesId, this.props.store.recurrenceInfo?.occurrenceStartDate)
                          .then(() => {
                            this.props.store.recurrenceInfo = undefined;
                            this.props.closeAndReload();
                          })
                          .catch(() => { })
                          .finally(() => {
                            this.props.store.loading = false;
                          });
                      }}
                      style={{
                        float: "right",
                        marginTop: 7,
                        marginRight: 15
                      }}
                    />
                  }
                </div>
              }
            </>
          }
          close={() => {
            this.props.store.serverAlert = undefined;
            this.setState({ imageViewerImages: undefined, draftSavedMessageUpdated: undefined });
            this.props.store.activeComponent = undefined;
            if (this.props.store.activeGalleryIndex) {
              //If gallery is currently uploading images when you want to close, we remove the gallery
              this.props.store.removeComponentAtIndex(this.props.store.activeGalleryIndex)
              this.props.store.activeGalleryIndex = undefined;
            }
            this.props.store.isGalleryImagesUploading = false;
            this.props.store.publishToReplay = false;
            this.props.store.replayContentTooHigh = false;
            this.props.close(this.convertToMessage());
          }}
        >
          {this.props.store.open &&
            <div
              className={styles.IA_composer}
              style={{
                height: window.innerHeight - 44 - this.props.store.marginTop
              }}
            >
              {!this.props.store.isMobile &&
                <Toolbox
                  onChange={() => this.forceUpdate()}
                  onSelect={(component: string) => {
                    this.props.store.addComponent({ type: component });
                  }}
                  onDragEnd={() => {
                    this.props.store.componentToAdd = undefined;
                  }}
                  onDrag={(component: string) => {
                    this.props.store.componentToAdd = {
                      type: component
                    };
                    this.props.store.activeComponent = undefined;
                    this.forceUpdate();
                  }}
                />
              }
              {this.props.store.isGalleryImagesUploading &&
                <div
                  style={{ backgroundColor: this.props.store.highlightColor, width: "100%" }}
                  className={styles.IA_uploadingGalleryImagesOverlay}
                >
                  <p className={styles.IA_uploadingGalleryImagesOverlayText}>
                    {this.localizationService.strings.Composer_UploadingImages}
                  </p>
                  <div style={{
                    width: 200,
                    height: 15,
                    backgroundColor: "rgba(0,0,0,0.8)",
                    borderRadius: 15,
                  }}>
                    <div style={{
                      height: "100%",
                      width: this.props.store.galleryImagesUploaded === undefined || this.props.store.galleryImagesUploaded === 0 ? 0 : `${((this.props.store.galleryImagesUploaded / this.props.store.galleryImagesToUploadCount) * 100)}%`,
                      backgroundColor: "white",
                      borderRadius: 15,
                      transition: "width 0.5s",
                      minWidth: "10%"
                    }}
                    />
                  </div>
                </div>
              }
              <div
                className={styles.IA_composerElements}
                style={{
                  width: this.props.store.isMobile ? "100%" : "calc(100% - 300px)"
                }}
              >
                <IAPivot
                  highlightColor={this.props.store.highlightColor}
                  items={tabs}
                  asTabs
                  showIcon={!this.props.store.isMobile}
                  tabBackgrondColor="#ebeaea"
                  selectedTabBackgrondColor="#ffffff"
                  selectedKey={this.props.store.selectedComposerTab}
                  select={(selectedTab: "content" | "settings" | "preview") => {
                    this.props.store.activeComponent = undefined;
                    this.props.store.selectedComposerTab = selectedTab;
                    this.forceUpdate();
                  }}
                  style={{
                    marginTop: 0,
                    height: 40,
                    marginBottom: 0
                  }}
                  isMobile={this.props.store.isMobile}
                />
                {this.props.store.serverAlert &&
                  <IAMessageBar
                    type={"warning"}
                    style={{
                      marginTop: 30,
                      marginBottom: 10,
                      borderRadius: 5,
                      background: "#ffda78",
                      marginLeft: 50,
                      marginRight: 45
                    }}
                    showCloseButton
                    content={
                      <div>
                        {this.props.store.serverAlert.message}
                      </div>
                    }
                    onDismiss={() => this.props.store.serverAlert = undefined}
                  />
                }
                {!this.props.store.validate() && this.getValidationErrors().length > 0 &&
                  <IAMessageBar
                    type={"error"}
                    style={{
                      marginTop: 10,
                      marginBottom: this.props.store.selectedComposerTab === "content" ? 0 : 10,
                      borderRadius: 5,
                      marginLeft: 10,
                      marginRight: 10,
                      padding: "0 15px",
                      width: "calc(100% - 50px)"
                    }}
                    icon="Attention"
                    showIcon
                    justifyContent="left"
                    content={
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column"
                        }}
                      >
                        {this.getValidationErrors().map(errorMessage => {
                          return <div>
                            {this.getValidationErrors().length > 1 ? "- " : ""}{errorMessage}
                          </div>;
                        })}
                      </div>
                    }
                    onDismiss={() => { }}
                  />
                }
                {this.state.showDraftValidationError &&
                  <IAMessageBar
                    type={"error"}
                    style={{
                      marginTop: 10,
                      marginBottom: this.props.store.selectedComposerTab === "content" ? 0 : 10,
                      borderRadius: 5,
                      marginLeft: 10,
                      marginRight: 10,
                      padding: "0 15px",
                      width: "calc(100% - 50px)"
                    }}
                    icon="Attention"
                    showIcon
                    justifyContent="left"
                    content={
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column"
                        }}
                      >
                        {this.localizationService.strings.Composer_ValidateDraft}
                      </div>
                    }
                    onDismiss={() => { }}
                  />
                }
                {this.state.draftSavedMessageUpdated &&
                  <IAMessageBar
                    type={"information"}
                    style={{
                      background: "#e8e8e8",
                      color: "#333333",
                      width: "calc(100% - 80px)",
                      margin: "20px 40px 10px"
                    }}
                    showCloseButton
                    content={
                      <div>
                        <span>{this.localizationService.strings.Composer_DraftWasSavedSuccessfullyAt}</span>
                        <Moment
                          fromNow
                          locale={navigator.language}
                        >
                          {this.state.draftSavedMessageUpdated}
                        </Moment>
                      </div>
                    }
                    onDismiss={() => this.setState({ draftSavedMessageUpdated: undefined })}
                  />
                }
                {this.props.store.selectedComposerTab === "content" &&
                  <>
                    {this.props.store.cover &&
                      <Cover
                        id={this.props.store.cover.id}
                        image={this.props.store.cover.image}
                        video={this.props.store.cover.video}
                        typeToEdit={this.props.store.activeComponent && this.props.store.activeComponent.typeToEdit}
                        hideToolbox={this.props.store.activeComponent && this.props.store.activeComponent.id !== this.props.store.cover.id}
                        showDropZones={this.props.store.componentToAdd != undefined}
                        isMobile={this.props.store.isMobile}
                        onChangeImage={(image: IComposerImage) => {
                          this.props.store.cover.image = image;
                          if (this.props.store.tenantSettings?.admin?.useBlobImages && !this.props.store.isMessagesTemplateEditor) {
                            Helper.getImageBlobUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, image.url, image.modified, "Full", image?.cropSettings).then((blobUrl) => {
                              this.props.store.cover.image.imageBlobUrl = blobUrl;
                              this.forceUpdate();
                            })
                          }
                          else {
                            this.forceUpdate();
                          }
                        }}
                        onChangeVideo={(video: IComposerVideo) => {
                          this.props.store.cover.video = video;
                          this.forceUpdate();
                        }}
                        onFocus={() => {
                          this.props.store.activeComponent = this.props.store.cover;
                          this.forceUpdate();
                        }}
                      />
                    }
                    {(this.props.store.categoryDataItems != undefined || (this.props.store.instance && this.props.store.instance.settings && this.props.store.instance.settings.categoriesRequired)) &&
                      <CategorySelector />
                    }
                    <div className={styles.IA_composerElement}>
                      <div className={styles.IA_label}>{this.localizationService.strings.Composer_Title} <span style={{ color: this.props.store.highlightColor }}>*</span></div>
                      {this.props.store.title &&
                        <>
                          <div className={styles.IA_title}>
                            <input
                              type="text"
                              id={this.props.store.title.id}
                              placeholder={this.localizationService.strings.Composer_TitlePlaceholderText}
                              value={this.props.store.title.text}
                              onChange={(event: any) => {
                                this.props.store.title.text = event.target.value;
                                this.forceUpdate();
                              }}
                              onFocus={() => {
                                this.props.store.activeComponent = this.props.store.title;
                              }}
                              style={{
                                border: this.props.store.activeComponent && this.props.store.activeComponent.type === "title" ? (!this.props.store.validateTitle() ? "2px solid red" : `2px solid ${this.props.store.highlightColor}`) : (!this.props.store.validateTitle() ? "1px solid red" : "1px solid rgb(203, 203, 203)"),
                                borderRadius: 5,
                                marginBottom: this.props.store.activeComponent && this.props.store.activeComponent.type === "title" ? 0 : 2,
                                fontFamily: this.props.store.fontSlots?.heading?.fontFamily
                              }}
                            />
                          </div>
                          {!this.props.store.validateTitle() &&
                            <div
                              className={styles.IA_composer_error_message}
                              style={{
                                clear: "both",
                                float: "left"
                              }}
                            >
                              {this.localizationService.strings.Composer_ValidateTitle}
                            </div>
                          }
                        </>
                      }
                    </div>
                    {this.props.store.component === "Event" &&
                      <div className={styles.IA_composerElement}>
                        <div className={styles.IA_label}>{this.localizationService.strings.Composer_Location}</div>
                        <div className={styles.IA_location}>
                          <input
                            type="text"
                            placeholder={this.localizationService.strings.Composer_LocationPlaceholderText}
                            value={this.props.store.location?.text}
                            onChange={(event: any) => {
                              if (this.props.store.activeComponent) {
                                this.props.store.location.text = event.target.value;
                                this.forceUpdate();
                              }
                            }}
                            onFocus={() => {
                              this.props.store.activeComponent = this.props.store.location;
                            }}
                            style={{
                              border: this.props.store.activeComponent && this.props.store.activeComponent.type === "location" ? `2px solid ${this.props.store.highlightColor}` : "1px solid rgb(203, 203, 203)",
                              marginBottom: this.props.store.activeComponent && this.props.store.activeComponent.type === "location" ? 0 : 2,
                              borderRadius: 5
                            }}
                          />
                        </div>
                      </div>
                    }
                    {this.props.store.instance?.settings?.enableTeaser &&
                      <Teaser onChange={() => this.forceUpdate()} />
                    }
                    {this.props.store.component !== "Event" &&
                      <Author
                        onChange={() => this.forceUpdate()}
                      />
                    }
                    {!this.props.store.validateContacts() && this.props.store.component !== "Event" &&
                      <div
                        className={styles.IA_composer_error_message}
                        style={{
                          clear: "both",
                          float: "left",
                          marginLeft: 40
                        }}
                      >
                        {this.localizationService.strings.Composer_ValidateContacts}
                      </div>
                    }
                    <div className={styles.IA_contentComponents}>
                      <div className={styles.IA_label}>{this.localizationService.strings.Composer_ToolboxContentTab}</div>
                      {this.renderContentComponents()}
                      <div
                        style={{
                          float: "left",
                          clear: "both",
                          width: "100%",
                          position: "relative"
                        }}
                      >
                        <div
                          className={styles.IA_contentComponentsInlineToolboxBotton}
                          onClick={() => {
                            this.props.store.activeComponent = undefined;
                            this.props.store.showLineToolbox = !this.props.store.showLineToolbox;
                            setTimeout(() => {
                              var my_element = document.getElementById("IA_InlineToolbox");
                              my_element.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                                inline: "nearest"
                              });
                            }, 0);
                          }}
                        >
                          <div
                            className={styles.IA_contentComponentsInlineToolboxLineLeft}
                            style={{
                              backgroundColor: this.props.store.highlightColor
                            }}
                          />
                          <IAIcon
                            title="Plus"
                            size={20}
                            color={this.props.store.highlightColor}
                            onClick={() => { }}
                            style={{
                              float: "left",
                              cursor: "pointer"
                            }}
                          />
                          <div
                            className={styles.IA_contentComponentsInlineToolboxLineRight}
                            style={{
                              backgroundColor: this.props.store.highlightColor
                            }}
                          />
                        </div>
                        <div
                          id="IA_InlineToolbox"
                          className={styles.IA_contentComponentsInlineToolbox}
                          style={{
                            display: this.props.store.showLineToolbox ? "block" : "none"
                          }}
                        >
                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "text" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDrag={(event) => {
                              event.preventDefault();
                            }}
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "text"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Paragraph"
                                size={40}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer"
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Paragraph}
                            </div>
                          </div>
                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "image" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "image"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Picture"
                                size={40}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer"
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Image}
                            </div>
                          </div>
                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "gallery" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "gallery"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="ImageGallery2"
                                size={40}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer"
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Gallery}
                            </div>
                          </div>
                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "video" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "video"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Video"
                                size={30}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  marginTop: 5,
                                  marginBottom: 5
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Video}
                            </div>
                          </div>

                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "audio" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "audio"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Audio"
                                size={30}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  marginTop: 5,
                                  marginBottom: 5
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Audio}
                            </div>
                          </div>


                          {/* <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.addComponent({ type: "table" });
                              this.props.store.showLineToolbox = false;
                            }}
                            draggable
                            onDragEnd={() => {
                              this.props.store.componentToAdd = undefined;
                            }}
                            onDragStart={() => {
                              this.props.store.componentToAdd = {
                                type: "table"
                              };
                              this.props.store.activeComponent = undefined;
                              this.forceUpdate();
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Table"
                                size={30}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  marginTop: 5,
                                  marginBottom: 5
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Table}
                            </div>
                          </div> */}

                          <div
                            className={styles.IA_toolButton}
                            onClick={() => {
                              this.props.store.activeComponent = this.props.store.attachments;
                              this.props.store.showLineToolbox = false;
                            }}
                          >
                            <div className={styles.IA_toolButtonIcon}>
                              <IAIcon
                                title="Attach"
                                size={30}
                                color={"#333333"}
                                onClick={() => { }}
                                style={{
                                  float: "left",
                                  cursor: "pointer",
                                  marginLeft: 5,
                                  marginTop: 5,
                                  marginBottom: 5
                                }} />
                            </div>
                            <div className={styles.IA_toolButtonLabel}>
                              {this.localizationService.strings.Composer_Attach}
                            </div>
                          </div>
                        </div>
                      </div>
                      {this.props.store.attachments && this.props.store.attachments.files && this.props.store.attachments.files.length > 0 &&
                        <IAAttachments
                          attachments={this.props.store.attachments.files}
                          linkColor={this.props.store.highlightColor}
                          style={{
                            margin: "15px 40px 0 0px",
                            float: "left",
                            width: "100%"
                          }}
                          onClick={() => this.props.store.activeComponent = this.props.store.attachments}
                        />
                      }
                    </div>
                  </>
                }
                {this.props.store.selectedComposerTab === "settings" &&
                  <Settings
                    onChange={() => this.forceUpdate()}
                    canCreateMorePin={this.state.canCreateMorePin}
                  />
                }
                {this.props.store.selectedComposerTab === "preview" &&
                  <Preview
                    imageViewerImagesToShow={(images: IComposerImage[]) => {
                      this.setState({ imageViewerImages: images });
                    }}
                    imageViewerIndexToShow={(index: number) => {
                      this.setState({ imageViewerIndex: index });
                    }}
                  />
                }
                {this.props.store.selectedComposerTab === "replay" &&
                  <Replay
                    onChange={() => this.forceUpdate()}
                  />
                }
                {this.props.store.selectedComposerTab === "newsLink" &&
                  <NewsLink
                    onChange={() => this.forceUpdate()}
                  />
                }
              </div>
              {this.props.store.loading &&
                <div className={styles.IA_savingOverlay}>
                  <div style={{
                    marginTop: "35%",
                    float: "left",
                    width: "100%"
                  }}
                  >
                    <IASpinner key={Helper.getRandomStringKey()} />
                  </div>
                </div>
              }
            </div>
          }
        </IAPanel>
        <IAPanel
          isMobile
          open={this.props.store.isMobile && this.props.store.activeComponent && (this.props.store.activeComponent.type === "image" || this.props.store.activeComponent.type === "video" || this.props.store.activeComponent.type === "audio" || this.props.store.activeComponent.type === "gallery" || this.props.store.activeComponent.type === "cover" || this.props.store.activeComponent.type === "subjects" || this.props.store.activeComponent.type === "attachments" || (this.props.store.activeComponent.type === "text" && this.props.store.showLinkEditor))}
          highlightColor={this.props.store.highlightColor}
          transition={"slide"}
          marginTop={this.props.store.marginTop}
          showNavigationBar={true}
          loading={this.props.store.isSaving}
          imageViewerImages={this.state.imageViewerImages}
          imageViewerIndex={this.state.imageViewerIndex}
          closeImageViewer={() => {
            this.setState({ imageViewerImages: undefined, imageViewerIndex: undefined });
          }}
          width={window.innerWidth}
          close={() => {
            this.props.store.serverAlert = undefined;
            this.setState({ imageViewerImages: undefined });
            this.props.store.activeComponent = undefined;
          }}
        >
          <Toolbox
            onChange={() => this.forceUpdate()}
            onSelect={(component: string) => {
              this.props.store.addComponent({ type: component });
            }}
          />
        </IAPanel>
        {this.props.store.showStockPhotos != undefined &&
          <StockPhotosPanel
            highlightColor={this.props.store.highlightColor}
            open={this.props.store.showStockPhotos}
            tenant={this.props.store.tenant}
            environment={this.props.store.environment}
            isMobile={this.props.store.isMobile}
            token={this.props.store.token}
            marginTop={this.props.store.marginTop}
            onSelect={(url: string) => {
              if (this.props.store.activeComponent.type === "video") {
                this.props.store.activeComponent.video.posterImage.url = url;
              } else {
                this.props.store.activeComponent.image.url = url;
                if (this.props.store.tenantSettings?.admin?.useBlobImages && !this.props.store.isMessagesTemplateEditor) {
                  Helper.getImageBlobUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.activeComponent.image.url, this.props.store.activeComponent.image.modified, "Full", this.props.store.activeComponent.image?.cropSettings).then((blobUrl) => {
                    this.props.store.activeComponent.image.imageBlobUrl = blobUrl;
                    this.forceUpdate();
                  })
                } else {
                  this.forceUpdate();
                }

              }
              this.props.store.showStockPhotos = false;
            }}
            close={() => {
              this.props.store.showStockPhotos = false;
            }}
          />
        }
        {this.props.store.showOrganizationalAssets != undefined &&
          <IAOrganizationalAssetPanel
            token={this.props.store.token}
            tenant={this.props.store.tenant}
            isMobile={this.props.store.isMobile}
            environment={this.props.store.environment}
            highlightColor={this.props.store.highlightColor}
            open={this.props.store.showOrganizationalAssets}
            marginTop={this.props.store.marginTop}
            onSelect={(url: string, modified: string) => {
              if (isVideo) {
                this.props.store.activeComponent.video.url = url;
              } else {
                this.props.store.activeComponent.image.url = url;
                if (this.props.store.tenantSettings?.admin?.useBlobImages && !this.props.store.isMessagesTemplateEditor) {
                  Helper.getImageBlobUrl(Environment.getEnvironmentForImageAPI(this.props.store.environment), this.props.store.tenant, this.props.store.activeComponent.image.url, this.props.store.activeComponent.image.modified, "Full", this.props.store.activeComponent.image?.cropSettings).then((blobUrl) => {
                    this.props.store.activeComponent.image.imageBlobUrl = blobUrl;
                    this.forceUpdate();
                  })
                } else {
                  this.forceUpdate();
                }
                this.props.store.activeComponent.image.modified = new Date(modified).toString().replace(/[^\d]/g, '');
              }
            }}
            close={() => {
              this.props.store.showOrganizationalAssets = false;
            }}
            fileTypeToDisplay={isVideo ? "video" : "image"}
          />
        }
      </div>
    );
  }

  public componentWillUnmount(): void {
    document.removeEventListener("keydown", (event) => this.keyListener(event), false);
  }

}